import React, { useState, useRef, useEffect } from "react";
import { X, Mail, Lock, User, Eye, EyeOff, ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function OtpInput({ value, onChange, length = 6 }) {
  const refs = useRef([]);
  // Create array of length digits, filling with value characters or empty strings
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  const handleChange = (i, char) => {
    if (!/^\d?$/.test(char)) return;
    const next = [...digits];
    next[i] = char;
    onChange(next.join(''));
    if (char && i < length - 1) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, length - 1);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div className="flex gap-2.5 justify-center" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          data-testid={`otp-input-${i}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d || ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          className="w-11 h-13 bg-[#1c1c26] border-2 border-[#2a2a38] rounded-lg text-center text-xl font-bold text-[#c8a96e] focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e] outline-none transition-all duration-200"
          style={{ fontFamily: 'monospace' }}
        />
      ))}
    </div>
  );
}

export default function AuthModal({ mode: initialMode, onClose }) {
  const [mode, setMode] = useState(initialMode || 'login');
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [showCoupon, setShowCoupon] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const MAX_RETRIES = 2;

  const withRetry = async (fn) => {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        return await fn();
      } catch (err) {
        if (attempt < MAX_RETRIES && (err.code === 'ERR_NETWORK' || !err.response || err.response?.status >= 500)) {
          await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
          continue;
        }
        throw err;
      }
    }
  };

  const getFriendlyError = (err) => {
    const detail = err.response?.data?.detail;
    if (detail) return detail;
    if (err.code === 'ERR_NETWORK' || !err.response) {
      return mode === 'login'
        ? 'Could not reach the server. Please try logging in again.'
        : 'Could not reach the server. Please try again.';
    }
    if (err.response?.status === 429) return 'Too many attempts. Please wait a moment and try again.';
    if (err.response?.status >= 500) return 'Something went wrong on our end. Please try again in a moment.';
    return 'Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) { setError('Name is required'); setLoading(false); return; }
        if (password.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
        // Step 1: Try to send OTP
        const otpRes = await withRetry(() => api.sendOtp({ email, name }));
        if (otpRes.data.message === 'skip_otp') {
          // Email service down — register directly without OTP
          const res = await withRetry(() => api.registerDirect({ email, password, name }));
          const { user, session_token } = res.data;
          login(user, session_token);
          toast.success(`Welcome to Resumiq.in, ${user.name?.split(' ')[0]}!`);
          if (couponCode.trim()) {
            try {
              const couponRes = await api.applyCoupon({ code: couponCode.trim(), plan: 'pro' });
              if (couponRes.data.activated) toast.success(couponRes.data.message);
            } catch {}
          }
          onClose();
          navigate('/generate', { state: { showOnboarding: true } });
        } else {
          toast.success('Verification code sent to your email!');
          setStep('otp');
          setResendTimer(30);
        }
      } else {
        // Login — direct
        const res = await withRetry(() => api.login({ email, password }));
        const { user, session_token } = res.data;
        login(user, session_token);
        toast.success(`Welcome back, ${user.name?.split(' ')[0]}!`);
        onClose();
        navigate('/generate');
      }
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 6) { setError('Please enter the full 6-digit code'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await withRetry(() => api.verifyOtp({ email, otp, name, password }));
      const { user, session_token } = res.data;
      login(user, session_token);
      toast.success(`Welcome to Resumiq.in, ${user.name?.split(' ')[0]}!`);

      // Apply coupon if provided
      if (couponCode.trim()) {
        try {
          const couponRes = await api.applyCoupon({ code: couponCode.trim(), plan: 'pro' });
          if (couponRes.data.activated) toast.success(couponRes.data.message);
          else toast.success(`Coupon applied! ${couponRes.data.message}`);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Coupon could not be applied');
        }
      }

      onClose();
      if (user.is_new_user) navigate('/generate', { state: { showOnboarding: true } });
      else navigate('/generate');
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setError('');
    setLoading(true);
    try {
      await withRetry(() => api.sendOtp({ email, name }));
      toast.success('New verification code sent!');
      setResendTimer(30);
      setOtp('');
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/generate';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(8px)', background: 'rgba(10,10,15,0.7)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md bg-[#13131a] border border-[#2a2a38] rounded-sm shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#2a2a38]">
          <div>
            {step === 'otp' ? (
              <>
                <h2 className="text-xl font-bold text-[#f0ede8]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Verify your email
                </h2>
                <p className="text-[#8a8799] text-xs mt-1">
                  Code sent to <span className="text-[#c8a96e]">{email}</span>
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-[#f0ede8]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {mode === 'login' ? 'Welcome back' : 'Create account'}
                </h2>
                <p className="text-[#8a8799] text-xs mt-1">
                  {mode === 'login' ? 'Sign in to your account' : 'Start for free, no credit card needed'}
                </p>
              </>
            )}
          </div>
          <button data-testid="auth-modal-close" onClick={onClose}
            className="text-[#8a8799] hover:text-[#f0ede8] transition-colors duration-200">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {step === 'otp' ? (
            /* ====== OTP VERIFICATION STEP ====== */
            <div className="space-y-5">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-[#c8a96e]/10 border border-[#c8a96e]/30 flex items-center justify-center">
                  <ShieldCheck size={28} className="text-[#c8a96e]" />
                </div>
              </div>

              <p className="text-center text-[#8a8799] text-sm">
                Enter the 6-digit code we sent to your email
              </p>

              <OtpInput value={otp} onChange={setOtp} />

              {error && (
                <div data-testid="auth-error" className="text-[#f87171] text-xs bg-[#f87171]/10 border border-[#f87171]/20 rounded-sm px-3 py-2 text-center">
                  {error}
                </div>
              )}

              <button
                data-testid="verify-otp-btn"
                onClick={handleVerifyOtp}
                disabled={loading || otp.length < 6}
                className="w-full py-3 bg-[#c8a96e] text-black font-medium rounded-sm hover:bg-[#e8c98e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200 text-sm"
                style={{ boxShadow: '0 0 15px rgba(200,169,110,0.2)' }}
              >
                {loading ? 'Verifying...' : 'Verify & Create Account'}
              </button>

              <div className="flex items-center justify-between">
                <button
                  data-testid="back-to-form-btn"
                  onClick={() => { setStep('form'); setOtp(''); setError(''); }}
                  className="text-[#8a8799] text-xs hover:text-[#f0ede8] flex items-center gap-1 transition-colors"
                >
                  <ArrowLeft size={12} /> Change email
                </button>
                <button
                  data-testid="resend-otp-btn"
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || loading}
                  className="text-xs text-[#c8a96e] hover:underline disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
                </button>
              </div>
            </div>
          ) : (
            /* ====== LOGIN / SIGNUP FORM ====== */
            <>
              {/* Tab switcher */}
              <div className="flex bg-[#1c1c26] rounded-sm p-1">
                {['login', 'signup'].map(m => (
                  <button
                    key={m}
                    data-testid={`auth-tab-${m}`}
                    onClick={() => { setMode(m); setError(''); setStep('form'); }}
                    className={`flex-1 py-2 text-sm font-medium rounded-sm transition-all duration-200 ${
                      mode === m ? 'bg-[#c8a96e] text-black' : 'text-[#8a8799] hover:text-[#f0ede8]'
                    }`}
                  >
                    {m === 'login' ? 'Log in' : 'Sign up'}
                  </button>
                ))}
              </div>

              {/* Google button */}
              <button
                data-testid="google-auth-btn"
                onClick={handleGoogleAuth}
                className="w-full flex items-center justify-center gap-3 py-3 bg-[#1c1c26] border border-[#2a2a38] rounded-sm text-[#f0ede8] text-sm hover:border-[#c8a96e]/50 hover:bg-[#1c1c26]/80 transition-all duration-200"
              >
                <svg width="16" height="16" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-[#2a2a38]" />
                <span className="text-[#8a8799] text-xs">or</span>
                <div className="flex-1 border-t border-[#2a2a38]" />
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                {mode === 'signup' && (
                  <div className="relative">
                    <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8799]" />
                    <input
                      data-testid="auth-name-input"
                      type="text"
                      placeholder="Full name"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      required
                      className="w-full bg-[#1c1c26] border border-[#2a2a38] rounded-sm pl-9 pr-4 py-3 text-sm text-[#f0ede8] placeholder:text-[#8a8799]/50 focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e] transition-colors duration-200"
                    />
                  </div>
                )}
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8799]" />
                  <input
                    data-testid="auth-email-input"
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="w-full bg-[#1c1c26] border border-[#2a2a38] rounded-sm pl-9 pr-4 py-3 text-sm text-[#f0ede8] placeholder:text-[#8a8799]/50 focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e] transition-colors duration-200"
                  />
                </div>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8799]" />
                  <input
                    data-testid="auth-password-input"
                    type={showPass ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full bg-[#1c1c26] border border-[#2a2a38] rounded-sm pl-9 pr-10 py-3 text-sm text-[#f0ede8] placeholder:text-[#8a8799]/50 focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e] transition-colors duration-200"
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a8799] hover:text-[#f0ede8] transition-colors">
                    {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>

                {error && (
                  <div data-testid="auth-error" className="text-[#f87171] text-xs bg-[#f87171]/10 border border-[#f87171]/20 rounded-sm px-3 py-2">
                    {error}
                  </div>
                )}

                {/* Coupon code - signup only */}
                {mode === 'signup' && (
                  <div>
                    {!showCoupon ? (
                      <button type="button" data-testid="show-coupon-btn" onClick={() => setShowCoupon(true)}
                        className="text-[#c8a96e] text-xs hover:underline underline-offset-2 transition-colors">
                        Have a coupon code?
                      </button>
                    ) : (
                      <div className="relative">
                        <input
                          data-testid="auth-coupon-input"
                          type="text"
                          placeholder="Enter coupon code"
                          value={couponCode}
                          onChange={e => setCouponCode(e.target.value.toUpperCase())}
                          className="w-full bg-[#1c1c26] border border-[#2a2a38] rounded-sm pl-3 pr-4 py-2.5 text-sm text-[#c8a96e] placeholder:text-[#8a8799]/50 focus:border-[#c8a96e] focus:ring-1 focus:ring-[#c8a96e] transition-colors duration-200 font-mono"
                        />
                      </div>
                    )}
                  </div>
                )}

                <button
                  data-testid="auth-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-[#c8a96e] text-black font-medium rounded-sm hover:bg-[#e8c98e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-sm"
                  style={{ boxShadow: '0 0 15px rgba(200,169,110,0.2)' }}
                >
                  {loading
                    ? (mode === 'signup' ? 'Sending verification code...' : 'Logging in...')
                    : (mode === 'signup' ? 'Continue with email' : 'Log in')
                  }
                </button>
              </form>

              <p className="text-center text-[#8a8799] text-xs">
                By continuing, you agree to our{' '}
                <span className="text-[#c8a96e] cursor-pointer hover:underline">Terms of Service</span>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
